import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { EmptyState } from './EmptyState.tsx';
import type { EmptyStateProps } from './EmptyState.tsx';
import { ErrorState } from './ErrorState.tsx';
import { SkeletonRows } from './Skeleton.tsx';
import { StatusDot } from './StatusDot.tsx';

/** The five states every data view designs for (DesignSystem 3.3). */
export type DataStateKind = 'loading' | 'empty' | 'partial' | 'error' | 'full';

export const DATA_STATES: readonly DataStateKind[] = ['loading', 'empty', 'partial', 'error', 'full'];

/**
 * Pick the state from what a view knows. Data already shown wins over a later error:
 * the error becomes a partial banner instead of blanking the view.
 */
export function dataStateOf(input: { loading?: boolean; error?: unknown; count?: number; missing?: number }): DataStateKind {
  const has = (input.count ?? 0) > 0;
  if (input.error != null && !has) return 'error';
  if (input.loading && !has) return 'loading';
  if (!has) return 'empty';
  if ((input.missing ?? 0) > 0 || input.error != null) return 'partial';
  return 'full';
}

export interface DataStateProps {
  state: DataStateKind;
  /** The real content: shown for full and partial. */
  children?: ReactNode;
  /** A skeleton in the final shape. Default: list rows. */
  skeleton?: ReactNode;
  /** What will appear, why it is empty, and the one action that fills it. */
  empty: Pick<EmptyStateProps, 'icon' | 'title' | 'description' | 'action'>;
  /** The quiet banner over partial data: "2 machines offline, data may be stale". */
  partial?: ReactNode;
  /** Anything thrown, or { what, why, next }. */
  error?: unknown;
  onRetry?: () => void | Promise<void>;
  /** Extra action beside Retry, usually a link to details. */
  errorAction?: ReactNode;
  /** Accessible name for the loading region. */
  loadingLabel?: string;
  className?: string;
}

/** One wrapper that renders loading, empty, partial, error and full, so pages never hand-roll them. */
export function DataState({
  state,
  children,
  skeleton,
  empty,
  partial,
  error,
  onRetry,
  errorAction,
  loadingLabel = 'Loading',
  className,
}: DataStateProps) {
  switch (state) {
    case 'loading':
      return (
        <div role="status" aria-busy="true" aria-label={loadingLabel} data-state="loading" className={className}>
          {skeleton ?? <SkeletonRows rows={4} />}
        </div>
      );
    case 'empty':
      return (
        <div data-state="empty" className={className}>
          <EmptyState {...empty} />
        </div>
      );
    case 'error':
      return (
        <div data-state="error" className={className}>
          <ErrorState error={error ?? { what: 'Could not load this.', why: 'The reason was not reported.', next: 'Try again.' }} onRetry={onRetry} action={errorAction} />
        </div>
      );
    case 'partial':
      return (
        <div data-state="partial" className={cn('flex min-w-0 flex-col', className)}>
          <div role="status" className="flex items-start gap-2 border-b border-border-subtle bg-raised px-4 py-2 text-ui text-fg-muted">
            <Info className="mt-0.5 size-4 shrink-0 text-status-attention" aria-hidden />
            <span className="min-w-0 flex-1">{partial ?? 'Some data is missing, so this may be incomplete.'}</span>
          </div>
          {children}
        </div>
      );
    case 'full':
      return (
        <div data-state="full" className={className}>
          {children}
        </div>
      );
  }
}

export type LiveMode = 'live' | 'paused' | 'reconnecting' | 'offline';

const LIVE = {
  live: { tone: 'working', label: 'Live', pulse: true },
  paused: { tone: 'idle', label: 'Paused', pulse: false },
  reconnecting: { tone: 'waiting', label: 'Reconnecting', pulse: true },
  offline: { tone: 'offline', label: 'Offline', pulse: false },
} as const;

/** The live state of a view: updates streaming, paused by the user, or the stream is down. */
export function LiveBadge({ mode, className }: { mode: LiveMode; className?: string }) {
  const m = LIVE[mode];
  return (
    <span role="status" className={cn('inline-flex items-center gap-1.5 text-caption font-medium text-fg-muted', className)}>
      <StatusDot tone={m.tone} pulse={m.pulse} size="xs" label="" />
      {m.label}
    </span>
  );
}
